﻿import { apiClient } from '../api/client';
import { FC, useState, FormEvent, ChangeEvent } from 'react';
import { Container, Section, Card, CardTitle, CardDescription, Button, Input, Alert, Badge } from '../components/ui';
import { SEOHead } from '../components/seo';
import { siteConfig } from '../config/site';
import { validateEmail, validateName, validatePhone } from '../utils/validation';
import { sanitizeString } from '../utils/sanitize';
import { trackContactSubmission, trackWhatsAppClick, trackConsultationClick } from '../analytics/events';
import { PageProps } from './HomePage';

export const ContactPage: FC<PageProps> = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('Career Coaching Inquiry');
  const [message, setMessage] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setGlobalError(null);

    const newErrors: Record<string, string> = {};

    const nameValidation = validateName(name);
    if (!nameValidation.isValid) {
      newErrors.name = nameValidation.error || 'Please enter your full name.';
    }

    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.error || 'Please provide a valid email address.';
    }

    if (phone.trim()) {
      const phoneValidation = validatePhone(phone);
      if (!phoneValidation.isValid) {
        newErrors.phone = phoneValidation.error || 'Please enter a valid phone number.';
      }
    }

    if (!message.trim() || message.trim().length < 10) {
      newErrors.message = 'Please provide a brief message explaining your inquiry (at least 10 characters).';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      
      const result = await apiClient.post<
        {
          fullName: string;
          email: string;
          phone: string;
          subject: string;
          message: string;
        },
        {
          success: boolean;
          data?: {
            inquiryId: string;
            message: string;
          };
          error?: {
            code: string;
            message: string;
          };
        }
      >('/contact-inquiries', {
        fullName: sanitizeString(name),
        email: email.trim(),
        phone: phone.trim(),
        subject: sanitizeString(subject),
        message: sanitizeString(message),
      });

      if (!result.success || !result.data?.success || !result.data.data?.inquiryId) {
        setGlobalError(
          result.data?.error?.message ||
          result.error?.message ||
          'We could not submit your inquiry. Please try again or email info@connectsource.co.in.'
        );
        return;
      }

      trackContactSubmission(sanitizeString(subject));
      trackConsultationClick('contact_form_submission', sanitizeString(subject));
      setSubmitted(true);

      
    } catch {
      setGlobalError(
        'We could not submit your inquiry. Please try again or email info@connectsource.co.in.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };



  const handleWhatsAppAction = () => {
    trackWhatsAppClick('contact_page_direct_chat', '/contact');
    window.open(`https://wa.me/${((siteConfig.whatsappNumber || siteConfig.supportPhone) as string).replace(/[^0-9]/g, '')}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <SEOHead
        title="Contact Us & Book a Consultation — Connect Source"
        description="Get in touch with Connect Source. Inquire about executive career coaching, B2B HR solutions, or get support with digital toolkits."
      />

      <Section style={{ backgroundColor: '#ffffff', borderBottom: '1px solid var(--color-border-subtle)', padding: '4.5rem 0' }}>
        <Container size="xl">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center', marginBottom: '3.5rem' }}>
            <Badge variant="primary" style={{ marginBottom: '1rem' }}>Direct Communication</Badge>
            <h1 style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3rem)', fontWeight: 800, color: 'var(--color-navy-dark)', lineHeight: 1.2 }}>
              Get In Touch With Connect Source
            </h1>
            <p style={{ fontSize: '1.15rem', color: 'var(--color-text-secondary)', marginTop: '1.25rem', lineHeight: 1.65 }}>
              Whether you are an ambitious professional seeking personalized coaching or a company founder exploring agile HR support, we are here to help.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '3.5rem', alignItems: 'start' }}>
            
            {/* Left Column: Direct Contact Details */}
            <div>
              <Card style={{ padding: '2rem', marginBottom: '2rem' }}>
                <CardTitle style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Direct Contact Information</CardTitle>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', fontSize: '0.95rem', color: 'var(--color-text-secondary)' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-navy-dark)', marginBottom: '0.2rem' }}>
                      Email Support:
                    </div>
                    <a href={`mailto:${siteConfig.contactEmail}`} style={{ fontWeight: 600, color: 'var(--color-brand-primary)' }}>
                      {siteConfig.contactEmail}
                    </a>
                  </div>

                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-navy-dark)', marginBottom: '0.2rem' }}>
                      Phone / WhatsApp:
                    </div>
                    <div style={{ fontWeight: 600, color: 'var(--color-navy-dark)' }}>
                      {siteConfig.supportPhone}
                    </div>
                  </div>
		
		
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-navy-dark)', marginBottom: '0.2rem' }}>
                      Business Location:
                    </div>
                    <div style={{ color: 'var(--color-text-secondary)' }}>
                      Mumbai, Maharashtra, India
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-navy-dark)', marginBottom: '0.2rem' }}>
                      GSTIN:
                    </div>
                    <div style={{ fontWeight: 600, color: 'var(--color-navy-dark)' }}>
                      {siteConfig.legal.gstin}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-navy-dark)', marginBottom: '0.2rem' }}>
                      Consultation Hours:
                    </div>
                    <div style={{ color: 'var(--color-text-secondary)' }}>
                      Monday – Saturday: 10:00 AM – 7:00 PM IST
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--color-border-subtle)' }}>
                  <Button
                    variant="teal"
                    size="md"
                    onClick={handleWhatsAppAction}
                    style={{ width: '100%' }}
                  >
                    Chat on WhatsApp →
                  </Button>
                </div>
              </Card>

              <div style={{ padding: '1.5rem', backgroundColor: 'var(--color-bg-canvas)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-subtle)' }}>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--color-navy-dark)', marginBottom: '0.5rem' }}>
                  Consultation Expectations
                </h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                  Initial discussions focus on diagnosing your specific challenges and providing realistic timelines. We never conduct aggressive sales calls or push unnecessary programs.
                </p>
              </div>
            </div>

            {/* Right Column: Interactive Form */}
            <div>
              <Card style={{ padding: '2.25rem' }}>
                {submitted ? (
                  <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                    <div
                      style={{
                        width: '3.5rem',
                        height: '3.5rem',
                        borderRadius: '50%',
                        backgroundColor: '#ecfdf5',
                        color: '#059669',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.75rem',
                        fontWeight: 800,
                        marginBottom: '1rem',
                        border: '1px solid #a7f3d0'
                      }}
                    >
                      ✓
                    </div>
                    <Badge variant="success" style={{ marginBottom: '0.75rem' }}>Message Received</Badge>
                    <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--color-navy-dark)' }}>
                      Thank You for Reaching Out
                    </h2>
                    <p style={{ fontSize: '0.95rem', color: 'var(--color-text-secondary)', marginTop: '0.75rem', lineHeight: 1.6 }}>
                      We have received your message. A member of the Connect Source team will review your inquiry and respond within 24 to 48 business hours.
                    </p>
                    <div style={{ marginTop: '2rem' }}>
                      <Button
                        variant="outline"
                        size="md"
                        onClick={() => {
                          setSubmitted(false);
                          setName('');
                          setEmail('');
                          setPhone('');
                          setMessage('');
                        }}
                      >
                        Send Another Inquiry
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <CardTitle style={{ fontSize: '1.35rem' }}>Send Us a Message</CardTitle>
                    <CardDescription style={{ marginTop: '0.35rem', marginBottom: '1.75rem', lineHeight: 1.5 }}>
                      Fill out the form below and we will get back to you promptly.
                    </CardDescription>

                    {globalError && (
                      <Alert variant="error" title="Submission Error">
                        {globalError}
                      </Alert>
                    )}

                    <form onSubmit={handleSubmit} noValidate>
                      <div style={{ marginBottom: '1.15rem' }}>
                        <Input
                          id="contact-name"
                          label="Full Name *"
                          type="text"
                          placeholder="e.g. Aditi Sharma"
                          value={name}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                          error={errors.name}
                          disabled={isSubmitting}
                          required
                        />
                      </div>

                      <div style={{ marginBottom: '1.15rem' }}>
                        <Input
                          id="contact-email"
                          label="Email Address *"
                          type="email"
                          placeholder="e.g. aditi@example.com"
                          value={email}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                          error={errors.email}
                          disabled={isSubmitting}
                          required
                        />
                      </div>

                      <div style={{ marginBottom: '1.15rem' }}>
                        <Input
                          id="contact-phone"
                          label="Phone / WhatsApp Number"
                          type="tel"
                          placeholder="e.g. +91 98765 43210"
                          value={phone}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
                          error={errors.phone}
                          disabled={isSubmitting}
                        />
                      </div>

                      <div style={{ marginBottom: '1.15rem' }}>
                        <label
                          htmlFor="contact-subject"
                          style={{
                            display: 'block',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: 'var(--color-navy-dark)',
                            marginBottom: '0.375rem'
                          }}
                        >
                          Subject / Inquiry Type *
                        </label>
                        <select
                          id="contact-subject"
                          className="cs-select"
                          value={subject}
                          onChange={(e: ChangeEvent<HTMLSelectElement>) => setSubject(e.target.value)}
                          disabled={isSubmitting}
                        >
                          <option value="Career Coaching Inquiry">Career Coaching & Mentorship</option>
                          <option value="Business HR Solutions">B2B HR Support & Recruitment</option>
                          <option value="Digital Toolkit Support">Digital Toolkit Support</option>
                          <option value="General Collaboration">General Inquiries & Collaboration</option>
                        </select>
                      </div>

                      <div style={{ marginBottom: '1.5rem' }}>
                        <label
                          htmlFor="contact-message"
                          style={{
                            display: 'block',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: 'var(--color-navy-dark)',
                            marginBottom: '0.375rem'
                          }}
                        >
                          How Can We Help You? *
                        </label>
                        <textarea
                          id="contact-message"
                          className="cs-textarea"
                          rows={4}
                          placeholder="Briefly describe your current situation or requirements..."
                          value={message}
                          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
                          disabled={isSubmitting}
                          required
                        />
                        {errors.message && (
                          <div style={{ fontSize: '0.8rem', color: '#dc2626', marginTop: '0.35rem' }}>
                            {errors.message}
                          </div>
                        )}
                      </div>

                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        style={{ width: '100%' }}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Sending Message...' : 'Submit Inquiry →'}
                      </Button>
                    </form>
                  </>
                )}
              </Card>
            </div>

          </div>
        </Container>
      </Section>
    </>
  );
};


